<template>
  <div class="excel-viewer">

    <div class="ev-toolbar" v-if="showToolbar && !loading && workbook">

      <div class="ev-sheet-selector" v-if="showSheetSelector && sheetNames.length > 1">
        <label class="ev-label">Sheet:</label>
        <select
          class="ev-select"
          :value="activeSheetName"
          @change="onSheetChange($event.target.value)"
        >
          <option
            v-for="name in sheetNames"
            :key="name"
            :value="name"
          >{{ name }}</option>
        </select>
      </div>

      <div class="ev-file-info" v-if="fileInfo">
        <span class="ev-file-name">{{ fileInfo.name }}</span>
        <span class="ev-sheet-count" v-if="sheetNames.length > 0">
          · {{ sheetNames.length }} sheet{{ sheetNames.length === 1 ? '' : 's' }}
        </span>
      </div>

      <div class="ev-selection-info" v-if="selectable && Object.keys(internalSelectedCells).length > 0">
        <span class="ev-selected-badge">
          {{ Object.keys(internalSelectedCells).length }} cell{{ Object.keys(internalSelectedCells).length === 1 ? '' : 's' }} selected
        </span>
      </div>

    </div>

    <div class="ev-selection-panel" v-if="selectable && lastSelectedCell">
      <div class="ev-info-box">
        <div class="ev-info-label">Sheet</div>
        <div class="ev-info-value">{{ lastSelectedCell.sheet }}</div>
      </div>
      <div class="ev-info-box">
        <div class="ev-info-label">Cell</div>
        <div class="ev-info-value">{{ lastSelectedCell.address }}</div>
      </div>
      <div class="ev-info-box">
        <div class="ev-info-label">Value</div>
        <div class="ev-info-value">{{ lastSelectedCell.value !== null && lastSelectedCell.value !== undefined ? lastSelectedCell.value : '(empty)' }}</div>
      </div>
      <div class="ev-info-box" v-if="lastSelectedCell.formula">
        <div class="ev-info-label">Formula</div>
        <div class="ev-info-value">={{ lastSelectedCell.formula }}</div>
      </div>
    </div>

    <div class="ev-loading" v-if="loading">
      <div class="ev-spinner"></div>
      <span>Reading spreadsheet...</span>
    </div>

    <div class="ev-error" v-if="error">
      <span>{{ error }}</span>
    </div>

    <div class="ev-empty" v-if="!loading && !error && !workbook">
      <slot name="empty">
        <span>No file loaded.</span>
      </slot>
    </div>

    <div class="ev-empty" v-if="!loading && !error && workbook && gridData.length === 0">
      <span>This worksheet is empty.</span>
    </div>

    <div
      class="ev-table-container"
      v-if="!loading && !error && workbook && gridData.length > 0"
      :style="{ maxHeight: height }"
      ref="tableContainer"
    >
      <table class="ev-table" :class="'ev-mode-' + selectionMode">
        <thead>
          <tr>
            <th class="ev-corner">#</th>
            <th
              class="ev-col-header"
              v-for="colIdx in columnCount"
              :key="'col-' + colIdx"
              :class="{
                'ev-selected': selectable && selectionMode === 'col' && internalSelectedCol === rangeStart.c + colIdx - 1,
                'ev-selectable': selectable && selectionMode === 'col',
                'ev-col-hover': selectable && selectionMode === 'col' && hoveredCol === colIdx - 1
              }"
              @click="onColClick(colIdx - 1)"
              @mouseenter="hoveredCol = colIdx - 1"
              @mouseleave="hoveredCol = null"
            >{{ getColumnLetter(rangeStart.c + colIdx - 1) }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(row, rIdx) in gridData" :key="'row-' + rIdx">
            <th
              class="ev-row-header"
              :class="{
                'ev-selected': selectable && selectionMode === 'row' && internalSelectedRow === rangeStart.r + rIdx + 1,
                'ev-selectable': selectable && selectionMode === 'row'
              }"
              @click="onRowClick(rIdx)"
            >{{ rangeStart.r + rIdx + 1 }}</th>
            <td
              v-for="(cell, cIdx) in row"
              :key="'cell-' + rIdx + '-' + cIdx"
              :class="{
                'ev-selected': selectionMode === 'row' ? internalSelectedRow === rangeStart.r + rIdx + 1 : (selectionMode === 'col' ? internalSelectedCol === rangeStart.c + cIdx : !!internalSelectedCells[cell.address]),
                'ev-selectable': selectable,
                'ev-col-hover': selectable && selectionMode === 'col' && hoveredCol === cIdx
              }"
              :title="cell.address"
              @click="onCellClick(rIdx, cIdx)"
              @mouseenter="hoveredCol = cIdx"
              @mouseleave="hoveredCol = null"
            >{{ cell.display }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="ev-confirm-bar" v-if="selectable && showConfirmButton && Object.keys(internalSelectedCells).length > 0">
      <button class="ev-btn-confirm" @click="confirmSelection">
        Confirm Selection
      </button>
    </div>

  </div>
</template>

<script>
import * as XLSX from 'xlsx'

export default {
  name: 'ExcelViewer',

  props: {
    file: {
      type: [File, Object],
      default: null
    },

    initialSheet: {
      type: String,
      default: ''
    },

    /**
     * Whether cells can be selected by clicking.
     */
    selectable: {
      type: Boolean,
      default: false
    },

    selectionMode: {
      type: String,
      default: 'cell',
      validator: (v) => ['cell', 'range', 'row', 'col'].includes(v)
    },

    selectedCells: {
      type: Object,
      default: () => ({})
    },

    showSheetSelector: {
      type: Boolean,
      default: true
    },

    height: {
      type: String,
      default: '500px'
    },

    editable: {
      type: Boolean,
      default: false
    },

    showConfirmButton: {
      type: Boolean,
      default: false
    },

    showToolbar: {
      type: Boolean,
      default: true
    }
  },

  data () {
    return {
      workbook: null,
      activeSheetName: '',
      sheetNames: [],
      gridData: [],
      rangeStart: { r: 0, c: 0 },
      columnCount: 0,
      loading: false,
      error: null,
      fileInfo: null,
      internalSelectedCells: {},
      internalSelectedRow: null,
      internalSelectedCol: null,
      hoveredCol: null,
      lastSelectedCell: null,
      worksheetRef: null
    }
  },

  watch: {
    file: {
      handler (newFile) {
        if (newFile) {
          this.loadFile(newFile)
        } else {
          this.resetState()
        }
      },
      immediate: true
    },

    selectedCells: {
      handler (newVal) {
        if (newVal && typeof newVal === 'object') {
          this.internalSelectedCells = { ...newVal }
        }
      },
      deep: true,
      immediate: true
    },

    initialSheet (newVal) {
      if (newVal && this.sheetNames.includes(newVal) && newVal !== this.activeSheetName) {
        this.renderSheet(newVal)
      }
    }
  },

  methods: {
    resetState () {
      this.workbook = null
      this.activeSheetName = ''
      this.sheetNames = []
      this.gridData = []
      this.rangeStart = { r: 0, c: 0 }
      this.columnCount = 0
      this.loading = false
      this.error = null
      this.fileInfo = null
      this.internalSelectedCells = {}
      this.internalSelectedRow = null
      this.internalSelectedCol = null
      this.hoveredCol = null
      this.lastSelectedCell = null
      this.worksheetRef = null
    },

    async loadFile (file) {
      this.resetState()
      this.loading = true
      this.error = null

      try {
        const arrayBuffer = await this.readFileAsArrayBuffer(file)

        this.workbook = XLSX.read(arrayBuffer, {
          type: 'array',
          cellFormula: true,
          cellStyles: true
        })

        if (!this.workbook.SheetNames || this.workbook.SheetNames.length === 0) {
          throw new Error('The workbook contains no worksheets.')
        }

        this.sheetNames = this.workbook.SheetNames

        this.fileInfo = {
          name: file.name,
          size: file.size
        }

        const targetSheet = this.initialSheet && this.sheetNames.includes(this.initialSheet)
          ? this.initialSheet
          : this.sheetNames[0]

        this.renderSheet(targetSheet)

        this.$emit('file-loaded', {
          fileName: file.name,
          sheetNames: [...this.sheetNames],
          sheetCount: this.sheetNames.length
        })
      } catch (err) {
        console.error('ExcelViewer: Error reading file', err)
        this.error = err.message || 'Could not read the spreadsheet.'
        this.$emit('error', {
          message: this.error,
          originalError: err
        })
      } finally {
        this.loading = false
      }
    },

    readFileAsArrayBuffer (file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = (e) => resolve(e.target.result)
        reader.onerror = (e) => reject(e)
        reader.readAsArrayBuffer(file)
      })
    },

    renderSheet (sheetName) {
      if (!this.workbook) return

      const worksheet = this.workbook.Sheets[sheetName]
      if (!worksheet) return

      this.activeSheetName = sheetName
      this.worksheetRef = worksheet
      this.gridData = []
      this.columnCount = 0

      this.internalSelectedCells = {}
      this.internalSelectedRow = null
      this.internalSelectedCol = null
      this.hoveredCol = null
      this.lastSelectedCell = null

      const reference = worksheet['!ref']

      if (!reference) {
        this.$emit('sheet-changed', { sheetName, isEmpty: true })
        return
      }

      const range = XLSX.utils.decode_range(reference)

      this.rangeStart = { r: range.s.r, c: range.s.c }
      this.columnCount = range.e.c - range.s.c + 1

      const grid = []

      for (let row = range.s.r; row <= range.e.r; row++) {
        const rowData = []

        for (let col = range.s.c; col <= range.e.c; col++) {
          const address = XLSX.utils.encode_cell({ r: row, c: col })
          const cell = worksheet[address]

          let display = ''
          if (cell) {
            if (cell.w !== undefined) {
              display = String(cell.w)
            } else if (cell.v !== undefined) {
              display = String(cell.v)
            }
          }

          rowData.push({
            address: address,
            display: display,
            row: row + 1,
            col: col,
            colLetter: XLSX.utils.encode_col(col),
            value: cell ? (cell.v !== undefined ? cell.v : null) : null,
            formattedValue: cell ? (cell.w !== undefined ? cell.w : null) : null,
            formula: cell ? (cell.f || null) : null,
            type: cell ? (cell.t || null) : null
          })
        }

        grid.push(rowData)
      }

      if (!this.editable) {
        this.gridData = Object.freeze(grid.map(row => Object.freeze(row)))
      } else {
        this.gridData = grid
      }

      this.$emit('sheet-changed', {
        sheetName,
        isEmpty: false,
        rowCount: range.e.r - range.s.r + 1,
        colCount: this.columnCount
      })
    },

    getColumnLetter (colIndex) {
      return XLSX.utils.encode_col(colIndex)
    },

    getCellAddress (rIdx, cIdx) {
      if (this.gridData[rIdx] && this.gridData[rIdx][cIdx]) {
        return this.gridData[rIdx][cIdx].address
      }
      return ''
    },

    isCellSelected (rIdx, cIdx) {
      const cellData = this.gridData[rIdx] && this.gridData[rIdx][cIdx]
      if (!cellData) return false
      return !!this.internalSelectedCells[cellData.address]
    },

    onCellClick (rIdx, cIdx) {
      if (!this.selectable) return

      if (this.selectionMode === 'row') {
        this.onRowClick(rIdx)
        return
      }

      if (this.selectionMode === 'col') {
        this.onColClick(cIdx)
        return
      }

      const cellData = this.gridData[rIdx] && this.gridData[rIdx][cIdx]
      if (!cellData) return

      const cellInfo = {
        sheet: this.activeSheetName,
        address: cellData.address,
        row: cellData.row,
        col: cellData.col,
        colLetter: cellData.colLetter,
        value: cellData.value,
        formattedValue: cellData.formattedValue,
        formula: cellData.formula,
        type: cellData.type
      }

      if (this.selectionMode === 'cell') {
        this.$set(this, 'internalSelectedCells', {
          [cellData.address]: cellInfo
        })
      } else {
        if (this.internalSelectedCells[cellData.address]) {
          const updated = { ...this.internalSelectedCells }
          delete updated[cellData.address]
          this.internalSelectedCells = updated
        } else {
          this.$set(this.internalSelectedCells, cellData.address, cellInfo)
        }
      }

      this.lastSelectedCell = cellInfo

      this.$emit('cell-selected', cellInfo)
    },

    onRowClick (rIdx) {
      if (!this.selectable || this.selectionMode !== 'row') return

      const rowNum = this.rangeStart.r + rIdx + 1

      this.internalSelectedRow = rowNum
      this.internalSelectedCol = null
      this.internalSelectedCells = {}
      this.lastSelectedCell = null

      this.$emit('row-selected', {
        sheet: this.activeSheetName,
        row: rowNum
      })
    },

    onColClick (cIdx) {
      if (!this.selectable || this.selectionMode !== 'col') return

      const colNum = this.rangeStart.c + cIdx

      this.internalSelectedCol = colNum
      this.internalSelectedRow = null
      this.internalSelectedCells = {}
      this.lastSelectedCell = null

      this.$emit('col-selected', {
        sheet: this.activeSheetName,
        col: colNum,
        colLetter: this.getColumnLetter(colNum)
      })
    },

    onSheetChange (sheetName) {
      this.renderSheet(sheetName)
    },

    confirmSelection () {
      this.$emit('selection-confirmed', {
        cells: { ...this.internalSelectedCells },
        sheet: this.activeSheetName
      })
    },

    setSheet (sheetName) {
      if (this.sheetNames.includes(sheetName)) {
        this.renderSheet(sheetName)
      }
    },

    clearSelection () {
      this.internalSelectedCells = {}
      this.internalSelectedRow = null
      this.internalSelectedCol = null
      this.hoveredCol = null
      this.lastSelectedCell = null
    },

    getSelection () {
      return {
        cells: { ...this.internalSelectedCells },
        sheet: this.activeSheetName
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.excel-viewer {
  display: flex;
  flex-direction: column;
  gap: 10px;
  font-family: "Nunito Sans", Arial, Helvetica, sans-serif;
  width: 100%;
}

.ev-toolbar {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: center;
  padding: 10px 14px;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
}

.ev-label {
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
}

.ev-sheet-selector {
  display: flex;
  align-items: center;
  gap: 8px;
}

.ev-select {
  min-width: 160px;
  padding: 6px 10px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background: #ffffff;
  font-size: 13px;
  font-family: inherit;
  color: #374151;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: #6366f1;
    box-shadow: 0 0 0 2px rgba(99, 102, 241, 0.15);
  }
}

.ev-file-info {
  font-size: 13px;
  color: #6b7280;
}

.ev-file-name {
  font-weight: 600;
  color: #374151;
}

.ev-sheet-count {
  color: #9ca3af;
}

.ev-selection-info {
  margin-left: auto;
}

.ev-selected-badge {
  font-size: 12px;
  font-weight: 600;
  color: #2563eb;
  background: #dbeafe;
  padding: 4px 10px;
  border-radius: 12px;
}

.ev-selection-panel {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.ev-info-box {
  flex: 1;
  min-width: 100px;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 8px 12px;
}

.ev-info-label {
  font-size: 10px;
  text-transform: uppercase;
  color: #9ca3af;
  letter-spacing: 0.05em;
  margin-bottom: 3px;
  font-weight: 600;
}

.ev-info-value {
  font-size: 13px;
  font-weight: 600;
  color: #374151;
  overflow-wrap: anywhere;
  word-break: break-all;
}

.ev-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 40px;
  color: #6b7280;
  font-size: 14px;
}

.ev-spinner {
  width: 20px;
  height: 20px;
  border: 2px solid #e5e7eb;
  border-top-color: #6366f1;
  border-radius: 50%;
  animation: ev-spin 0.7s linear infinite;
}

@keyframes ev-spin {
  to { transform: rotate(360deg); }
}

.ev-error {
  padding: 20px;
  text-align: center;
  color: #dc2626;
  font-size: 14px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 8px;
}

.ev-empty {
  padding: 40px;
  text-align: center;
  color: #9ca3af;
  font-size: 14px;
}

.ev-table-container {
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: auto;

  &::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }
  &::-webkit-scrollbar-track {
    background: #f9fafb;
  }
  &::-webkit-scrollbar-thumb {
    background: #d1d5db;
    border-radius: 4px;

    &:hover {
      background: #9ca3af;
    }
  }
}

.ev-table {
  border-collapse: separate;
  border-spacing: 0;
  min-width: max-content;
  font-size: 13px;

  th, td {
    border-right: 1px solid #e5e7eb;
    border-bottom: 1px solid #e5e7eb;
    padding: 6px 12px;
    min-width: 90px;
    height: 32px;
    white-space: nowrap;
  }

  thead th {
    position: sticky;
    top: 0;
    background: #f3f4f6;
    text-align: center;
    font-weight: 600;
    font-size: 12px;
    color: #6b7280;
    z-index: 3;
    user-select: none;
  }

  .ev-corner {
    position: sticky;
    top: 0;
    left: 0;
    min-width: 50px;
    width: 50px;
    z-index: 5;
    background: #f3f4f6;
    text-align: center;
    font-weight: 600;
    color: #9ca3af;
  }

  .ev-row-header {
    position: sticky;
    left: 0;
    min-width: 50px;
    width: 50px;
    background: #f3f4f6;
    text-align: center;
    font-weight: 500;
    font-size: 12px;
    color: #6b7280;
    z-index: 2;
    user-select: none;
  }

  td {
    background: #ffffff;
    color: #374151;
  }

  td.ev-selectable,
  th.ev-selectable {
    cursor: pointer;
  }

  &.ev-mode-cell td.ev-selectable:not(.ev-selected):hover,
  &.ev-mode-range td.ev-selectable:not(.ev-selected):hover {
    background: #eff6ff;
  }

  &.ev-mode-row tbody tr:hover td.ev-selectable:not(.ev-selected),
  &.ev-mode-row tbody tr:hover th.ev-selectable:not(.ev-selected) {
    background: #eff6ff;
  }

  &.ev-mode-col .ev-col-hover:not(.ev-selected) {
    background: #eff6ff;
  }

  td.ev-selected,
  th.ev-selected {
    background: #dbeafe;
    outline: 2px solid #2563eb;
    outline-offset: -2px;
    position: relative;
    z-index: 1;
  }
}

.ev-confirm-bar {
  display: flex;
  justify-content: flex-end;
  padding: 8px 0 0;
}

.ev-btn-confirm {
  display: flex;
  align-items: center;
  gap: 6px;
  background-color: #6366f1;
  color: #ffffff;
  border: none;
  border-radius: 7px;
  padding: 8px 20px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #4f46e5;
  }

  &:active {
    background-color: #4338ca;
  }
}
</style>
